import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  Timestamp
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { Prize, PrizeRedemption, PrizeFormData } from '../types';
import { pointsService } from './pointsService';
import { Capacitor } from '@capacitor/core';
import { firestoreRestApi } from '../utils/firestoreRestApi';

class PrizeService {
  private prizesCollection = collection(db, 'prizes');
  private redemptionsCollection = collection(db, 'prizeRedemptions');

  // Prize CRUD operations
  async createPrize(prizeData: PrizeFormData, teamId: string, coachId: string): Promise<string> {
    // #region agent log
    const authUid = auth.currentUser?.uid ?? null;
    fetch('http://127.0.0.1:7691/ingest/e518302a-d81e-45f6-a1c0-7d77bc1f2e9e',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'607d16'},body:JSON.stringify({sessionId:'607d16',location:'prizeService.ts:createPrize',message:'createPrize entry',data:{teamId,coachId,authUid},timestamp:Date.now(),hypothesisId:'B,C,E'})}).catch(()=>{});
    // #endregion
    const prize: any = {
      name: prizeData.name,
      description: prizeData.description,
      pointCost: prizeData.pointCost,
      type: prizeData.type,
      category: prizeData.category,
      teamId,
      coachId,
      isActive: true,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    // Only include optional fields if they have values
    if (prizeData.imageUrl) {
      prize.imageUrl = prizeData.imageUrl;
    }
    
    if (prizeData.maxQuantity) {
      prize.maxQuantity = prizeData.maxQuantity;
      prize.currentQuantity = prizeData.maxQuantity;
    }

    // On iOS, use REST API first (SDK can hang due to auth)
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const restPayload: Record<string, any> = {
          name: prize.name,
          description: prize.description,
          pointCost: prize.pointCost,
          type: prize.type,
          category: prize.category,
          teamId: prize.teamId,
          coachId: prize.coachId,
          isActive: prize.isActive,
          createdAt: prize.createdAt?.toDate?.() ?? new Date(),
          updatedAt: prize.updatedAt?.toDate?.() ?? new Date()
        };
        if (prize.imageUrl) restPayload.imageUrl = prize.imageUrl;
        if (prize.maxQuantity != null) restPayload.maxQuantity = prize.maxQuantity;
        if (prize.currentQuantity != null) restPayload.currentQuantity = prize.currentQuantity;
        const docId = await firestoreRestApi.createDocument('prizes', restPayload);
        return docId;
      } catch (err: any) {
        console.error('iOS REST createPrize failed:', err);
        throw err;
      }
    }

    try {
      const docRef = await addDoc(this.prizesCollection, prize);
      // #region agent log
      fetch('http://127.0.0.1:7691/ingest/e518302a-d81e-45f6-a1c0-7d77bc1f2e9e',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'607d16'},body:JSON.stringify({sessionId:'607d16',location:'prizeService.ts:createPrize',message:'createPrize addDoc success',data:{prizeId:docRef.id},timestamp:Date.now(),hypothesisId:'A'})}).catch(()=>{});
      // #endregion
      return docRef.id;
    } catch (err: any) {
      // #region agent log
      fetch('http://127.0.0.1:7691/ingest/e518302a-d81e-45f6-a1c0-7d77bc1f2e9e',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'607d16'},body:JSON.stringify({sessionId:'607d16',location:'prizeService.ts:createPrize',message:'createPrize addDoc error',data:{code:err?.code,message:err?.message,authUid:auth.currentUser?.uid},timestamp:Date.now(),hypothesisId:'A,B,C'})}).catch(()=>{});
      // #endregion
      if (err?.code === 'permission-denied' || err?.message?.includes('permission')) {
        try {
          console.warn('Permission denied via SDK for createPrize; retrying via REST API');
          const restPayload: Record<string, any> = {
            name: prize.name,
            description: prize.description,
            pointCost: prize.pointCost,
            type: prize.type,
            category: prize.category,
            teamId: prize.teamId,
            coachId: prize.coachId,
            isActive: prize.isActive,
            createdAt: prize.createdAt?.toDate?.() ?? new Date(),
            updatedAt: prize.updatedAt?.toDate?.() ?? new Date()
          };
          if (prize.imageUrl) restPayload.imageUrl = prize.imageUrl;
          if (prize.maxQuantity != null) restPayload.maxQuantity = prize.maxQuantity;
          if (prize.currentQuantity != null) restPayload.currentQuantity = prize.currentQuantity;
          const docId = await firestoreRestApi.createDocument('prizes', restPayload);
          return docId;
        } catch (restErr: any) {
          console.error('REST fallback failed for createPrize:', restErr);
        }
      }
      throw err;
    }
  }

  async updatePrize(prizeId: string, prizeData: Partial<PrizeFormData>): Promise<void> {
    const updateData: any = {
      updatedAt: Timestamp.now()
    };
    if (prizeData.name !== undefined) updateData.name = prizeData.name;
    if (prizeData.description !== undefined) updateData.description = prizeData.description;
    if (prizeData.pointCost !== undefined) updateData.pointCost = prizeData.pointCost;
    if (prizeData.type !== undefined) updateData.type = prizeData.type;
    if (prizeData.category !== undefined) updateData.category = prizeData.category;
    if (prizeData.imageUrl !== undefined) updateData.imageUrl = prizeData.imageUrl;
    if (prizeData.maxQuantity !== undefined) {
      updateData.maxQuantity = prizeData.maxQuantity;
      updateData.currentQuantity = prizeData.maxQuantity;
    }

    // On iOS, use REST API first (SDK can hang due to auth)
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const restPayload: Record<string, any> = {
          updatedAt: updateData.updatedAt?.toDate?.() ?? new Date()
        };
        if (updateData.name !== undefined) restPayload.name = updateData.name;
        if (updateData.description !== undefined) restPayload.description = updateData.description;
        if (updateData.pointCost !== undefined) restPayload.pointCost = updateData.pointCost;
        if (updateData.type !== undefined) restPayload.type = updateData.type;
        if (updateData.category !== undefined) restPayload.category = updateData.category;
        if (updateData.imageUrl !== undefined) restPayload.imageUrl = updateData.imageUrl;
        if (updateData.maxQuantity !== undefined) restPayload.maxQuantity = updateData.maxQuantity;
        if (updateData.currentQuantity !== undefined) restPayload.currentQuantity = updateData.currentQuantity;
        await firestoreRestApi.setDocument('prizes', prizeId, restPayload, true);
        return;
      } catch (err: any) {
        console.error('iOS REST updatePrize failed:', err);
        throw err;
      }
    }

    const prizeRef = doc(this.prizesCollection, prizeId);
    try {
      await updateDoc(prizeRef, updateData);
    } catch (err: any) {
      if (err?.code === 'permission-denied' || err?.message?.includes('permission') || err?.message?.includes('insufficient permissions')) {
        try {
          console.warn('Permission denied via SDK for updatePrize; retrying via REST API');
          const restPayload: Record<string, any> = {
            updatedAt: updateData.updatedAt?.toDate?.() ?? new Date()
          };
          if (updateData.name !== undefined) restPayload.name = updateData.name;
          if (updateData.description !== undefined) restPayload.description = updateData.description;
          if (updateData.pointCost !== undefined) restPayload.pointCost = updateData.pointCost;
          if (updateData.type !== undefined) restPayload.type = updateData.type;
          if (updateData.category !== undefined) restPayload.category = updateData.category;
          if (updateData.imageUrl !== undefined) restPayload.imageUrl = updateData.imageUrl;
          if (updateData.maxQuantity !== undefined) restPayload.maxQuantity = updateData.maxQuantity;
          if (updateData.currentQuantity !== undefined) restPayload.currentQuantity = updateData.currentQuantity;
          await firestoreRestApi.setDocument('prizes', prizeId, restPayload, true);
          return;
        } catch (restErr: any) {
          console.error('REST fallback failed for updatePrize:', restErr);
        }
      }
      throw err;
    }
  }

  async deletePrize(prizeId: string): Promise<void> {
    // On iOS, use REST API first (SDK can hang due to auth)
    if (Capacitor.getPlatform() === 'ios') {
      try {
        await firestoreRestApi.deleteDocument('prizes', prizeId);
        return;
      } catch (err: any) {
        console.error('iOS REST deletePrize failed:', err);
        throw err;
      }
    }

    const prizeRef = doc(this.prizesCollection, prizeId);
    try {
      await deleteDoc(prizeRef);
    } catch (err: any) {
      if (err?.code === 'permission-denied' || err?.message?.includes('permission') || err?.message?.includes('insufficient permissions')) {
        try {
          console.warn('Permission denied via SDK for deletePrize; retrying via REST API');
          await firestoreRestApi.deleteDocument('prizes', prizeId);
          return;
        } catch (restErr: any) {
          console.error('REST fallback failed for deletePrize:', restErr);
          throw restErr;
        }
      }
      throw err;
    }
  }

  async getPrizeById(prizeId: string): Promise<Prize | null> {
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const prizeDoc = await firestoreRestApi.getDocument('prizes', prizeId);
        if (!prizeDoc) return null;
        return { id: prizeDoc.id, ...prizeDoc.data() } as Prize;
      } catch (error) {
        console.error('Error fetching prize via REST API:', error);
        return null;
      }
    }
    
    // On web/Android, use SDK with REST fallback on permission denied
    const prizeRef = doc(this.prizesCollection, prizeId);
    try {
      const prizeSnap = await getDoc(prizeRef);
      if (prizeSnap.exists()) {
        return { id: prizeSnap.id, ...prizeSnap.data() } as Prize;
      }
      return null;
    } catch (err: any) {
      const isPermissionError = err?.code === 'permission-denied' || err?.message?.includes('permission') || err?.message?.includes('insufficient permissions');
      if (isPermissionError) {
        try {
          const prizeDoc = await firestoreRestApi.getDocument('prizes', prizeId);
          if (!prizeDoc) return null;
          return { id: prizeDoc.id, ...prizeDoc.data() } as Prize;
        } catch (restErr) {
          console.error('REST fallback for getPrizeById failed:', restErr);
          return null;
        }
      }
      throw err;
    }
  }

  async getPrizesByTeam(teamId: string): Promise<Prize[]> {
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const documents = await firestoreRestApi.queryDocuments(
          'prizes',
          [
            { field: 'teamId', op: '==', value: teamId },
            { field: 'isActive', op: '==', value: true }
          ]
        );
        
        // Sort by pointCost ascending (client-side)
        const sorted = documents.sort((a, b) => {
          const aCost = a.data().pointCost || 0;
          const bCost = b.data().pointCost || 0;
          return aCost - bCost;
        });
        
        return sorted.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Prize[];
      } catch (error) {
        console.error('Error fetching prizes via REST API:', error);
        return [];
      }
    }
    
    // On web/Android, use SDK
    const q = query(
      this.prizesCollection,
      where('teamId', '==', teamId),
      where('isActive', '==', true),
      orderBy('pointCost', 'asc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Prize[];
  }

  async getIndividualPrizes(teamId: string): Promise<Prize[]> {
    // #region agent log
    const authUid = auth.currentUser?.uid ?? null;
    fetch('http://127.0.0.1:7691/ingest/e518302a-d81e-45f6-a1c0-7d77bc1f2e9e',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'607d16'},body:JSON.stringify({sessionId:'607d16',location:'prizeService.ts:getIndividualPrizes',message:'getIndividualPrizes entry',data:{teamId,platform:Capacitor.getPlatform(),authUid},timestamp:Date.now(),hypothesisId:'A,B,E'})}).catch(()=>{});
    // #endregion
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const documents = await firestoreRestApi.queryDocuments(
          'prizes',
          [
            { field: 'teamId', op: '==', value: teamId },
            { field: 'type', op: '==', value: 'individual' },
            { field: 'isActive', op: '==', value: true }
          ]
        );
        
        // Sort by pointCost ascending (client-side)
        const sorted = documents.sort((a, b) => {
          const aCost = a.data().pointCost || 0;
          const bCost = b.data().pointCost || 0;
          return aCost - bCost;
        });
        
        return sorted.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Prize[];
      } catch (error) {
        console.error('Error fetching individual prizes via REST API:', error);
        return [];
      }
    }
    
    // On web/Android, use SDK
    const q = query(
      this.prizesCollection,
      where('teamId', '==', teamId),
      where('type', '==', 'individual'),
      where('isActive', '==', true),
      orderBy('pointCost', 'asc')
    );
    try {
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Prize[];
    } catch (err: any) {
      // #region agent log
      fetch('http://127.0.0.1:7691/ingest/e518302a-d81e-45f6-a1c0-7d77bc1f2e9e',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'607d16'},body:JSON.stringify({sessionId:'607d16',location:'prizeService.ts:getIndividualPrizes',message:'getIndividualPrizes getDocs error',data:{code:err?.code,message:err?.message,authUid:auth.currentUser?.uid},timestamp:Date.now(),hypothesisId:'A,B,D'})}).catch(()=>{});
      // #endregion
      if (err?.code === 'permission-denied' || err?.message?.includes('permission')) {
        try {
          console.warn('Permission denied via SDK for getIndividualPrizes; retrying via REST API');
          const documents = await firestoreRestApi.queryDocuments('prizes', [
            { field: 'teamId', op: '==', value: teamId },
            { field: 'type', op: '==', value: 'individual' },
            { field: 'isActive', op: '==', value: true }
          ]);
          const sorted = documents.sort((a: any, b: any) => ((a.data().pointCost || 0) - (b.data().pointCost || 0)));
          return sorted.map((d: any) => ({ id: d.id, ...d.data() })) as Prize[];
        } catch (restErr: any) {
          console.error('REST fallback failed for getIndividualPrizes:', restErr);
        }
      }
      throw err;
    }
  }

  async getTeamPrizes(teamId: string): Promise<Prize[]> {
    // #region agent log
    const authUid = auth.currentUser?.uid ?? null;
    fetch('http://127.0.0.1:7691/ingest/e518302a-d81e-45f6-a1c0-7d77bc1f2e9e',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'607d16'},body:JSON.stringify({sessionId:'607d16',location:'prizeService.ts:getTeamPrizes',message:'getTeamPrizes entry',data:{teamId,platform:Capacitor.getPlatform(),authUid},timestamp:Date.now(),hypothesisId:'A,B,E'})}).catch(()=>{});
    // #endregion
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const documents = await firestoreRestApi.queryDocuments(
          'prizes',
          [
            { field: 'teamId', op: '==', value: teamId },
            { field: 'type', op: '==', value: 'team' },
            { field: 'isActive', op: '==', value: true }
          ]
        );
        
        // Sort by pointCost ascending (client-side)
        const sorted = documents.sort((a, b) => {
          const aCost = a.data().pointCost || 0;
          const bCost = b.data().pointCost || 0;
          return aCost - bCost;
        });
        
        return sorted.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Prize[];
      } catch (error) {
        console.error('Error fetching team prizes via REST API:', error);
        return [];
      }
    }
    
    // On web/Android, use SDK
    const q = query(
      this.prizesCollection,
      where('teamId', '==', teamId),
      where('type', '==', 'team'),
      where('isActive', '==', true),
      orderBy('pointCost', 'asc')
    );
    try {
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Prize[];
    } catch (err: any) {
      // #region agent log
      fetch('http://127.0.0.1:7691/ingest/e518302a-d81e-45f6-a1c0-7d77bc1f2e9e',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'607d16'},body:JSON.stringify({sessionId:'607d16',location:'prizeService.ts:getTeamPrizes',message:'getTeamPrizes getDocs error',data:{code:err?.code,message:err?.message,authUid:auth.currentUser?.uid},timestamp:Date.now(),hypothesisId:'A,B,D'})}).catch(()=>{});
      // #endregion
      if (err?.code === 'permission-denied' || err?.message?.includes('permission')) {
        try {
          console.warn('Permission denied via SDK for getTeamPrizes; retrying via REST API');
          const documents = await firestoreRestApi.queryDocuments('prizes', [
            { field: 'teamId', op: '==', value: teamId },
            { field: 'type', op: '==', value: 'team' },
            { field: 'isActive', op: '==', value: true }
          ]);
          const sorted = documents.sort((a: any, b: any) => ((a.data().pointCost || 0) - (b.data().pointCost || 0)));
          return sorted.map((d: any) => ({ id: d.id, ...d.data() })) as Prize[];
        } catch (restErr: any) {
          console.error('REST fallback failed for getTeamPrizes:', restErr);
        }
      }
      throw err;
    }
  }

  // Prize redemption operations
  async requestPrizeRedemption(
    prizeId: string, 
    userId: string, 
    teamId: string, 
    pointsSpent: number
  ): Promise<string> {
    // Check if user can afford the prize
    let canAfford = await this.canUserAffordPrize(userId, teamId, pointsSpent);
    if (!canAfford) {
      throw new Error('Insufficient points to redeem this prize');
    }

    // Check prize availability
    const availability = await this.getPrizeAvailability(prizeId);
    if (!availability.available) {
      throw new Error('Prize is not available');
    }

    // Re-check affordability with fresh read immediately before spending (avoids race / stale data)
    canAfford = await this.canUserAffordPrize(userId, teamId, pointsSpent);
    if (!canAfford) {
      throw new Error('Insufficient points to redeem this prize');
    }

    // Spend the points
    await pointsService.spendPoints(userId, teamId, pointsSpent, `Prize redemption: ${prizeId}`);

    const redemption: Omit<PrizeRedemption, 'id'> = {
      prizeId,
      userId,
      teamId,
      pointsSpent,
      status: 'pending',
      requestedAt: Timestamp.now(),
      createdAt: Timestamp.now()
    };

    // On iOS, use REST API first (SDK can hang due to auth)
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const restPayload = {
          prizeId,
          userId,
          teamId,
          pointsSpent,
          status: 'pending',
          requestedAt: redemption.requestedAt?.toDate?.() ?? new Date(),
          createdAt: redemption.createdAt?.toDate?.() ?? new Date()
        };
        const docId = await firestoreRestApi.createDocument('prizeRedemptions', restPayload);
        return docId;
      } catch (err: any) {
        console.error('iOS REST requestPrizeRedemption failed:', err);
        throw err;
      }
    }

    try {
      const docRef = await addDoc(this.redemptionsCollection, redemption);
      return docRef.id;
    } catch (err: any) {
      const isPermissionError = err?.code === 'permission-denied' || err?.message?.includes('permission') || err?.message?.includes('insufficient permissions');
      if (isPermissionError) {
        try {
          console.warn('Permission denied via SDK for requestPrizeRedemption; retrying via REST API');
          const restPayload = {
            prizeId,
            userId,
            teamId,
            pointsSpent,
            status: 'pending',
            requestedAt: redemption.requestedAt?.toDate?.() ?? new Date(),
            createdAt: redemption.createdAt?.toDate?.() ?? new Date()
          };
          const docId = await firestoreRestApi.createDocument('prizeRedemptions', restPayload);
          return docId;
        } catch (restErr: any) {
          console.error('REST fallback failed for requestPrizeRedemption:', restErr);
          throw restErr;
        }
      }
      throw err;
    }
  }

  async getRedemptionsByUser(userId: string): Promise<PrizeRedemption[]> {
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const documents = await firestoreRestApi.queryDocuments(
          'prizeRedemptions',
          [{ field: 'userId', op: '==', value: userId }]
        );
        
        // Sort by requestedAt descending (client-side)
        const sorted = documents.sort((a, b) => {
          const aTime = a.data().requestedAt?.seconds || 0;
          const bTime = b.data().requestedAt?.seconds || 0;
          return bTime - aTime;
        });
        
        return sorted.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as PrizeRedemption[];
      } catch (error) {
        console.error('Error fetching redemptions via REST API:', error);
        return [];
      }
    }
    
    // On web/Android, use SDK with REST fallback on permission-denied
    const q = query(
      this.redemptionsCollection,
      where('userId', '==', userId),
      orderBy('requestedAt', 'desc')
    );

    try {
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as PrizeRedemption[];
    } catch (err: any) {
      const isPermissionError =
        err?.code === 'permission-denied' ||
        err?.message?.includes('permission') ||
        err?.message?.includes('insufficient permissions');

      if (isPermissionError) {
        try {
          console.warn('Permission denied via SDK for getRedemptionsByUser; retrying via REST API');
          const documents = await firestoreRestApi.queryDocuments('prizeRedemptions', [
            { field: 'userId', op: '==', value: userId }
          ]);
          const sorted = documents.sort((a: any, b: any) => {
            const aTime = a.data().requestedAt?.seconds ?? 0;
            const bTime = b.data().requestedAt?.seconds ?? 0;
            return bTime - aTime;
          });
          return sorted.map((d: any) => ({ id: d.id, ...d.data() })) as PrizeRedemption[];
        } catch (restErr: any) {
          console.error('REST fallback failed for getRedemptionsByUser:', restErr);
          throw restErr;
        }
      }

      throw err;
    }
  }

  async getRedemptionsByTeam(teamId: string): Promise<PrizeRedemption[]> {
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const documents = await firestoreRestApi.queryDocuments(
          'prizeRedemptions',
          [{ field: 'teamId', op: '==', value: teamId }]
        );
        
        // Sort by requestedAt descending (client-side)
        const sorted = documents.sort((a, b) => {
          const aTime = a.data().requestedAt?.seconds || 0;
          const bTime = b.data().requestedAt?.seconds || 0;
          return bTime - aTime;
        });
        
        return sorted.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as PrizeRedemption[];
      } catch (error) {
        console.error('Error fetching team redemptions via REST API:', error);
        return [];
      }
    }
    
    // On web/Android, use SDK with REST fallback on permission-denied (e.g. coach on web)
    const q = query(
      this.redemptionsCollection,
      where('teamId', '==', teamId),
      orderBy('requestedAt', 'desc')
    );
    try {
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as PrizeRedemption[];
    } catch (err: any) {
      if (err?.code === 'permission-denied' || err?.message?.includes('permission') || err?.message?.includes('insufficient permissions')) {
        try {
          console.warn('Permission denied via SDK for getRedemptionsByTeam; retrying via REST API');
          const documents = await firestoreRestApi.queryDocuments('prizeRedemptions', [
            { field: 'teamId', op: '==', value: teamId }
          ]);
          const sorted = documents.sort((a: any, b: any) => {
            const aTime = a.data().requestedAt?.seconds ?? 0;
            const bTime = b.data().requestedAt?.seconds ?? 0;
            return bTime - aTime;
          });
          return sorted.map((d: any) => ({ id: d.id, ...d.data() })) as PrizeRedemption[];
        } catch (restErr: any) {
          console.error('REST fallback failed for getRedemptionsByTeam:', restErr);
          throw restErr;
        }
      }
      throw err;
    }
  }

  async getPendingRedemptions(teamId: string): Promise<PrizeRedemption[]> {
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const documents = await firestoreRestApi.queryDocuments(
          'prizeRedemptions',
          [
            { field: 'teamId', op: '==', value: teamId },
            { field: 'status', op: '==', value: 'pending' }
          ]
        );
        
        // Sort by requestedAt ascending (client-side)
        const sorted = documents.sort((a, b) => {
          const aTime = a.data().requestedAt?.seconds || 0;
          const bTime = b.data().requestedAt?.seconds || 0;
          return aTime - bTime;
        });
        
        return sorted.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as PrizeRedemption[];
      } catch (error) {
        console.error('Error fetching pending redemptions via REST API:', error);
        return [];
      }
    }
    
    // On web/Android, use SDK
    const q = query(
      this.redemptionsCollection,
      where('teamId', '==', teamId),
      where('status', '==', 'pending'),
      orderBy('requestedAt', 'asc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as PrizeRedemption[];
  }

  async approveRedemption(redemptionId: string, notes?: string): Promise<void> {
    const payload = {
      status: 'approved',
      approvedAt: Timestamp.now(),
      notes: notes || ''
    };
    if (Capacitor.getPlatform() === 'ios') {
      try {
        await firestoreRestApi.setDocument('prizeRedemptions', redemptionId, {
          status: 'approved',
          approvedAt: payload.approvedAt?.toDate?.() ?? new Date(),
          notes: payload.notes || ''
        }, true);
        return;
      } catch (err: any) {
        console.error('iOS REST approveRedemption failed:', err);
        throw err;
      }
    }
    const redemptionRef = doc(this.redemptionsCollection, redemptionId);
    try {
      await updateDoc(redemptionRef, payload);
    } catch (err: any) {
      if (err?.code === 'permission-denied' || err?.message?.includes('permission') || err?.message?.includes('insufficient permissions')) {
        try {
          console.warn('Permission denied via SDK for approveRedemption; retrying via REST API');
          await firestoreRestApi.setDocument('prizeRedemptions', redemptionId, {
            status: 'approved',
            approvedAt: payload.approvedAt?.toDate?.() ?? new Date(),
            notes: payload.notes
          }, true);
          return;
        } catch (restErr: any) {
          console.error('REST fallback failed for approveRedemption:', restErr);
          throw restErr;
        }
      }
      throw err;
    }
  }

  async denyRedemption(redemptionId: string, notes?: string): Promise<void> {
    let redemption: PrizeRedemption;
    const redemptionRef = doc(this.redemptionsCollection, redemptionId);

    if (Capacitor.getPlatform() === 'ios') {
      try {
        const restDoc = await firestoreRestApi.getDocument('prizeRedemptions', redemptionId);
        if (!restDoc) throw new Error('Redemption not found');
        redemption = { id: restDoc.id, ...restDoc.data() } as PrizeRedemption;
        await pointsService.refundPoints(redemption.userId, redemption.teamId, redemption.pointsSpent, `Prize redemption denied: ${redemptionId}`);
        await firestoreRestApi.setDocument('prizeRedemptions', redemptionId, {
          status: 'denied',
          approvedAt: new Date(),
          notes: notes || ''
        }, true);
        return;
      } catch (err: any) {
        console.error('iOS REST denyRedemption failed:', err);
        throw err;
      }
    }

    try {
      const redemptionDoc = await getDoc(redemptionRef);
      if (!redemptionDoc.exists()) throw new Error('Redemption not found');
      redemption = { id: redemptionDoc.id, ...redemptionDoc.data() } as PrizeRedemption;
    } catch (err: any) {
      if (err?.code === 'permission-denied' || err?.message?.includes('permission') || err?.message?.includes('insufficient permissions')) {
        try {
          const restDoc = await firestoreRestApi.getDocument('prizeRedemptions', redemptionId);
          if (!restDoc) throw new Error('Redemption not found');
          redemption = { id: restDoc.id, ...restDoc.data() } as PrizeRedemption;
        } catch (restErr: any) {
          console.error('REST fallback for get redemption in denyRedemption failed:', restErr);
          throw restErr;
        }
      } else throw err;
    }

    await pointsService.refundPoints(
      redemption.userId,
      redemption.teamId,
      redemption.pointsSpent,
      `Prize redemption denied: ${redemptionId}`
    );

    const updatePayload = {
      status: 'denied',
      approvedAt: Timestamp.now(),
      notes: notes || ''
    };
    try {
      await updateDoc(redemptionRef, updatePayload);
    } catch (err: any) {
      if (err?.code === 'permission-denied' || err?.message?.includes('permission') || err?.message?.includes('insufficient permissions')) {
        try {
          await firestoreRestApi.setDocument('prizeRedemptions', redemptionId, {
            status: 'denied',
            approvedAt: updatePayload.approvedAt?.toDate?.() ?? new Date(),
            notes: updatePayload.notes
          }, true);
          return;
        } catch (restErr: any) {
          console.error('REST fallback failed for denyRedemption update:', restErr);
          throw restErr;
        }
      }
      throw err;
    }
  }

  async fulfillRedemption(redemptionId: string, notes?: string): Promise<void> {
    const payload = {
      status: 'fulfilled',
      fulfilledAt: Timestamp.now(),
      notes: notes || ''
    };
    let prizeId: string | null = null;
    const redemptionRef = doc(this.redemptionsCollection, redemptionId);
    try {
      if (Capacitor.getPlatform() === 'ios') {
        const restDoc = await firestoreRestApi.getDocument('prizeRedemptions', redemptionId);
        if (restDoc) prizeId = restDoc.data()?.prizeId ?? null;
      } else {
        const snap = await getDoc(redemptionRef);
        if (snap.exists()) prizeId = (snap.data() as any)?.prizeId ?? null;
      }
    } catch (_) { /* use prizeId as null if fetch fails */ }

    if (Capacitor.getPlatform() === 'ios') {
      try {
        await firestoreRestApi.setDocument('prizeRedemptions', redemptionId, {
          status: 'fulfilled',
          fulfilledAt: payload.fulfilledAt?.toDate?.() ?? new Date(),
          notes: payload.notes || ''
        }, true);
        if (prizeId) await this.decrementPrizeQuantityIfLimited(prizeId);
        return;
      } catch (err: any) {
        console.error('iOS REST fulfillRedemption failed:', err);
        throw err;
      }
    }
    try {
      await updateDoc(redemptionRef, payload);
      if (prizeId) await this.decrementPrizeQuantityIfLimited(prizeId);
    } catch (err: any) {
      if (err?.code === 'permission-denied' || err?.message?.includes('permission') || err?.message?.includes('insufficient permissions')) {
        try {
          console.warn('Permission denied via SDK for fulfillRedemption; retrying via REST API');
          await firestoreRestApi.setDocument('prizeRedemptions', redemptionId, {
            status: 'fulfilled',
            fulfilledAt: payload.fulfilledAt?.toDate?.() ?? new Date(),
            notes: payload.notes
          }, true);
          if (prizeId) await this.decrementPrizeQuantityIfLimited(prizeId);
          return;
        } catch (restErr: any) {
          console.error('REST fallback failed for fulfillRedemption:', restErr);
          throw restErr;
        }
      }
      throw err;
    }
  }

  private async decrementPrizeQuantityIfLimited(prizeId: string): Promise<void> {
    const prize = await this.getPrizeById(prizeId);
    if (!prize?.maxQuantity) return;
    const current = Number(prize.currentQuantity ?? prize.maxQuantity ?? 0);
    const next = Math.max(0, current - 1);
    const prizeRef = doc(this.prizesCollection, prizeId);
    try {
      await updateDoc(prizeRef, {
        currentQuantity: next,
        updatedAt: Timestamp.now()
      });
    } catch (err: any) {
      if (err?.code === 'permission-denied' || err?.message?.includes('permission') || err?.message?.includes('insufficient permissions')) {
        try {
          await firestoreRestApi.setDocument('prizes', prizeId, {
            currentQuantity: next,
            updatedAt: new Date()
          }, true);
        } catch (restErr: any) {
          console.error('REST fallback for decrement prize quantity failed:', restErr);
        }
      }
    }
  }

  // Utility functions
  async canUserAffordPrize(userId: string, teamId: string, prizeCost: number): Promise<boolean> {
    try {
      const userPoints = await pointsService.getUserPoints(userId, teamId);
      if (!userPoints) return false;
      // Purchasing power = wallet points (spendable balance)
      const walletPoints = Number(userPoints.walletPoints ?? userPoints.availablePoints ?? (userPoints.points ?? 0) - (userPoints.spentPoints ?? 0));
      const cost = Number(prizeCost);
      return Number.isFinite(walletPoints) && Number.isFinite(cost) && cost > 0 && walletPoints >= cost;
    } catch (error) {
      console.error('Error checking if user can afford prize:', error);
      return false;
    }
  }

  async getPrizeAvailability(prizeId: string): Promise<{ available: boolean; remaining?: number }> {
    const prize = await this.getPrizeById(prizeId);
    if (!prize) {
      return { available: false };
    }

    if (!prize.maxQuantity) {
      return { available: true };
    }

    const remaining = (prize.currentQuantity || 0);
    return {
      available: remaining > 0,
      remaining
    };
  }

  // Get redemption with prize details
  async getRedemptionWithPrize(redemptionId: string): Promise<{
    redemption: PrizeRedemption;
    prize: Prize | null;
  }> {
    const redemptionRef = doc(this.redemptionsCollection, redemptionId);
    const redemptionSnap = await getDoc(redemptionRef);
    
    if (!redemptionSnap.exists()) {
      throw new Error('Redemption not found');
    }

    const redemption = { id: redemptionSnap.id, ...redemptionSnap.data() } as PrizeRedemption;
    const prize = await this.getPrizeById(redemption.prizeId);

    return { redemption, prize };
  }
}

export const prizeService = new PrizeService();
